import React from 'react';
import { useField } from 'formik';
import { clsx } from 'clsx';

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  name: string;
  label?: string;
  helperText?: string;
  options: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  containerClassName?: string;
}

export function Select({
  name,
  label,
  helperText,
  options,
  placeholder,
  containerClassName,
  className,
  required,
  disabled,
  ...props
}: SelectProps) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label && (
        <label
          htmlFor={name}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <select
        id={name}
        {...field}
        {...props}
        disabled={disabled}
        className={clsx(
          'block w-full rounded-lg border transition-colors duration-200',
          'px-4 py-2.5 text-base',
          'text-gray-900 dark:text-white',
          'bg-white dark:bg-dark-800',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'appearance-none bg-right bg-no-repeat',
          'cursor-pointer',
          hasError
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-dark-600',
          disabled && 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-dark-900',
          className
        )}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: 'right 0.5rem center',
          backgroundSize: '1.5em 1.5em',
        }}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {hasError && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{meta.error}</p>}

      {helperText && !hasError && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
